<template>
  <div
    class="question-details-wrapper"
    :class="
      question.precondition || question.type === 'merged' ? 'follow-up' : ''
    "
  >
    <div
      class="question-card card d-flex flex-column justify-content-between pt-3 h-100"
      :class="question.precondition ? 'bg-gray-200' : ''"
    >
      <div class="question-details-body">
        <div
          v-if="anyQuestionHasHistory && question.type !== 'merged'"
          class="graph-selector-wrapper"
        >
          <QuestionDetailsFlipper
            v-model="flipped"
            :question="question"
            :disabled="!detailsFlipperEnabled"
          />
        </div>

        <div
          v-if="!isIndividualGroup && question.type !== 'merged'"
          class="p-4 bar-chart-container"
        >
          <BarChart
            v-if="!flipped"
            :dataName="`question-details-bar-${question.name}-${parentContainerId || ''}`"
            :data="barChartData"
            :options="{
              fullScreen,
              height: 380,
              // height: question.type === 'merged' ? 700 : 380,
              width: 600,
              rotateXAxisLabels: question.type === 'merged'
            }"
            :numberOfResponses="question.numberOfResponses"
          />
          <div v-if="lineChartData && flipped" class="mb-15">
            <LineChart
              :dataName="`question-details-line-${question.name}-${parentContainerId || ''}`"
              :data="lineChartData.data"
              :series="lineChartData.series"
              :options="{
                percentage: question.type === 'yesOrNo',
                fullScreen,
                largeFontSize: lineChartData.options.largeFontSize,
                fractionalDigits: lineChartData.options.fractionalDigits,
                ticks: lineChartData.options.ticks,
                domain: lineChartData.options.domain,
                height: 380,
                width: 800
              }"
            />
          </div>
        </div>
        <div v-else-if="question.type === 'merged'" class="p-4">
          <BarChartHorizontal
            v-if="barChartData.length"
            :dataName="`question-details-bar-horizontal-${question.name}-${parentContainerId || ''}`"
            :data="barChartData"
            :options="{
              fullScreen,
              height: 440,
              // height: question.type === 'merged' ? 700 : 380,
              width: 800
            }"
          />
          <div
            v-else
            class="col alert alert-info text-info d-flex align-items-center"
          >
            <i class="bi bi-info-circle-fill fs-3 text-info me-3"></i>
            <span v-if="allMergedQuestionsHaveSameLabel">
              {{
                `${$t(
                  'categoryDetails.tooltip.nooneFollowupQuestionsSameLabel',
                  {
                    yesLabel: generalYesOrNoValueLabel
                  }
                )}`
              }}
            </span>
            <span v-else>
              {{
                `${$t(
                  'categoryDetails.tooltip.nooneFollowupQuestionsRelevant'
                )}`
              }}
            </span>
          </div>
        </div>

        <IndividualGroupAnswer
          v-if="
            isIndividualGroup &&
            !question.hasRanges &&
            question.type !== 'merged'
          "
          :question="question"
          :yesOrNoValue="individualYesOrNoValueLabel"
        />
        <div class="ms-5 mb-4 me-4 d-flex flex-column justify-content-end">
          <QuestionDetailsTitle
            :isIndividualGroup="isIndividualGroup"
            :question="question"
            :labelData="barChartData"
          />
          <PulsFormCheck
            v-if="question.type !== 'merged' && shouldShowOwnAnswersCheckbox"
            :id="`${question.name}-show`"
            v-model="showFollowupQuestions"
            :label="$t('categoryDetails.showFollowUpQuestions')"
          />
          <PulsFormCheck
            v-if="shouldShowMergeFollowupQuestionsCheckbox"
            :id="`${question.name}-merge`"
            v-model="showFollowupAsMerged"
            :label="$t('categoryDetails.showFollowUpQuestionsCondensed')"
          />
        </div>

        <div
          v-if="!fullScreen && question.numberOfSensitiveQuestions > 0"
          class="ms-5 mb-4 text-info sensitive-text d-flex align-items-center justify-content-start"
        >
          <i class="bi bi-info-circle-fill text-info fs-5 me-2"></i>
          {{ $t('categoryDetails.sensitiveQuestion') }}
        </div>
      </div>
      <div
        v-if="!fullScreen && question.type !== 'merged'"
        class="question-details-footer card-footer px-5"
      >
        <div class="stats d-flex justify-content-between">
          <div class="d-flex align-items-center">
            <i class="bi bi-people-fill fs-3 me-3"></i>
            <span
              v-if="shouldShowNumberOfPeople"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              :title="$t('categoryDetails.numberOfPeople')"
            >
              {{ question.numberOfResponses }}
            </span>
          </div>
          <div
            v-if="!shouldHideOwnResponses && question.ownResponse"
            class="d-flex align-items-center"
          >
            <i class="bi bi-person-fill fs-3 me-3"></i>
            <i
              v-if="questionIsPartOfQuizAndCorrectAnswer"
              class="badge rounded-pill text-bg-success"
            >
            </i>
            <span>
              {{ $t('categoryDetails.yourResponse') }}
              {{ ownResponseValueForQuestion }}
            </span>
          </div>
          <div v-if="Object.keys(question.aggregateInfo || {}).length === 1">
            <div class="d-flex align-items-center">
              <i class="bi bi-calendar3-event fs-3 me-3"></i>
              <div class="d-flex gap-2">
                <span
                  v-for="surveyName in Object.keys(question.aggregateInfo)"
                  :key="surveyName"
                  class="cursor-help"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  :title="
                    $t('widgetCategoriesBars.aggregateInfo.question.info', {
                      surveyDate:
                        question.aggregateInfo[surveyName].end_date.split(
                          'T'
                        )[0],
                      surveyTitle: question.aggregateInfo[surveyName].title
                    })
                  "
                >
                  {{
                    question.aggregateInfo[surveyName].end_date.split('T')[0]
                  }}
                </span>
              </div>
            </div>
          </div>
          <div v-else-if="Object.keys(question.aggregateInfo || {}).length > 1">
            <i
              class="bi bi-calendar3-event fs-3 me-3"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              :title="`${$t(
                'widgetCategoriesBars.aggregateInfo.question.resultsFromManyAggregates'
              )} ${Object.keys(question.aggregateInfo)
                .map(
                  (surveyName) =>
                    question.aggregateInfo[surveyName].end_date.split('T')[0]
                )
                .join(', ')}`"
            ></i>
          </div>
          <div
            v-if="!question.hasRanges"
            class="d-flex align-items-center"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            :title="
              question.type === 'yesOrNo'
                ? ''
                : $t('categoryDetails.average', {
                    average: $localeNumber(question.value),
                    benchmark: comparedToBenchmarkText
                  })
            "
          >
            <i class="bi bi-bar-chart-fill fs-3 me-3"></i>
            <span v-if="question.type === 'yesOrNo'">
              {{ question.numberOfPeopleSelectedYes }}
              ({{ question.percentageYes }} %)
              {{ $t('categoryDetails.chartLabel.yesLabel') }}
            </span>
            <span v-else-if="question.type === 'zeroToTen'">
              {{ $localeNumber(question.value) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BarChart from '@/components/shared/charts/BarChart.vue'
import BarChartHorizontal from '@/components/shared/charts/BarChartHorizontal.vue'
import LineChart from '@/components/shared/charts/LineChart.vue'
import QuestionDetailsFlipper from './QuestionDetailsFlipper.vue'
import QuestionDetailsTitle from './QuestionDetailsTitle.vue'
import IndividualGroupAnswer from './IndividualGroupAnswer.vue'
import {
  charForRangeOrder,
  getLabelsForQuestion
} from '@/helpers/question-helpers'

import {
  getBarChartDataForStatItem,
  getLineChartDataForStatItems
} from '@/components/shared/charts/chart-helpers'

import { getComparedToBenchmarkText } from '@/helpers/results-helper'

export default {
  name: 'QuestionDetailsZeroToTen',
  components: {
    BarChart,
    BarChartHorizontal,
    IndividualGroupAnswer,
    LineChart,
    QuestionDetailsFlipper,
    QuestionDetailsTitle
  },
  props: {
    parentContainerId: {
      type: String,
      default: ''
    },
    question: {
      type: Object,
      default: () => {}
    },
    followUpQuestionsState: {
      type: Object,
      default: () => {}
    },
    shouldShowFollowUpCheckbox: {
      type: Boolean,
      default: false
    },
    shouldShowMergeCheckbox: {
      type: Boolean,
      default: false
    },
    didSelectShowFollowupQuestions: {
      type: Function,
      default: null
    },
    didSelectMergeFollowupQuestions: {
      type: Function,
      default: null
    },
    isIndividualGroup: {
      type: Boolean,
      default: false
    },
    shouldHideOwnResponses: {
      type: Boolean,
      default: true
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    anyQuestionHasHistory: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      flipped: false,
      showFollowupQuestions: false,
      // By default, show follow-up questions in a condensed (merged)
      // view for a cleaner UI.
      showFollowupAsMerged: true
    }
  },

  computed: {
    firstMergedQuestionHasYesIsBadConfig() {
      return Object.values(
        this.question.mergedQuestions || {}
      )[0]?.hasOwnProperty('yesIsBad')
    },
    allMergedQuestionsHaveSameLabel() {
      if (
        this.question.type === 'merged' &&
        this.firstMergedQuestionHasYesIsBadConfig
      ) {
        const firstQuestionYesIsBadValue =
          Object.values(this.question.mergedQuestions)[0].yesIsBad || false
        return Object.values(this.question.mergedQuestions).every(
          (question) => {
            return question.yesIsBad === firstQuestionYesIsBadValue
          }
        )
      }
      return null
    },
    generalYesOrNoValueLabel() {
      if (this.question.type === 'yesOrNo' || this.question.type === 'merged') {
        const key = this.question.yesIsBad ? 1 : 0
        const type =
          this.question.type === 'merged' ? 'yesOrNo' : this.question.type
        return getLabelsForQuestion({
          t: this.$t,
          type,
          rangeKeys: this.question.rangeKeys,
          yesIsBad: this.question.yesIsBad
        })[key]
      }
      return null
    },
    individualYesOrNoValueLabel() {
      if (this.question.type === 'yesOrNo') {
        const hasAnsweredYes = this.question.numberOfPeopleSelectedYes === 1
        const yesKey = this.question.yesIsBad ? 0 : 1
        const noKey = this.question.yesIsBad ? 1 : 0
        const key = hasAnsweredYes ? yesKey : noKey

        const type = this.question.type
        return {
          value: hasAnsweredYes ? 'yes' : 'no',
          label: getLabelsForQuestion({
            t: this.$t,
            type,
            rangeKeys: this.question.rangeKeys,
            yesIsBad: this.question.yesIsBad
          })[key]
        }
      }
      return null
    },
    barChartData() {
      if (this.question.type === 'merged') {
        return Object.values(this.question.mergedQuestions).reduce(
          (res, question) => {
            const barChartDataForMerged = getBarChartDataForStatItem({
              statItem: question,
              t: this.$t
            })
            if (question.yesIsBad) {
              res.push({
                value: barChartDataForMerged[0].value,
                name: this.$tr(question.messages, 'title'),
                label: barChartDataForMerged[0].name
              })
            } else {
              res.push({
                value: barChartDataForMerged[0].value,
                name: this.$tr(question.messages, 'title'),
                label: barChartDataForMerged[0].name
              })
            }
            return res
          },
          []
        )
      } else {
        return getBarChartDataForStatItem({
          statItem: this.question,
          t: this.$t
        })
      }
    },

    comparedToBenchmarkText() {
      return getComparedToBenchmarkText({
        t: this.$t,
        tr: this.$tr,
        localeNumber: this.$localeNumber,
        item: this.question
      })
    },
    lineChartData() {
      return getLineChartDataForStatItems({
        statItems: { [this.question.name]: this.question },
        hasLinearRegression: this.question.regressionValues?.length > 2,
        shouldFormatZeroToHundred: this.question.type === 'yesOrNo',
        isQuestion: true,
        tr: this.$tr
      })
    },
    ownResponseValueForQuestion() {
      let value = '-'
      if (this.question.hasRanges) {
        for (var i = 0; i < this.question.rangeKeys.length; i++) {
          const k = this.question.rangeKeys[i]
          if (parseInt(k) === parseInt(this.question.ownResponse)) {
            value = charForRangeOrder(i)
          }
        }
      } else if (this.question.type === 'yesOrNo') {
        value = getLabelsForQuestion({
          t: this.$t,
          type: this.question.type,
          rangeKeys: this.question.rangeKeys,
          yesIsBad: this.question.yesIsBad
        })[this.question.ownResponse]
      } else {
        value = this.question.ownResponse
      }
      return value
    },
    shouldShowNumberOfPeople() {
      return (
        (this.question.numberOfPeople === 0 && this.question.average === '-') ||
        (this.question.numberOfPeople !== 0 &&
          this.question.averageForAnswers === this.question.average) ||
        (this.question.numberOfPeople !== 0 &&
          this.question.average === this.$localeNumber(this.question.rawValue))
      )
    },
    shouldShowOwnAnswersCheckbox() {
      if (this.isIndividualGroup) {
        const individualHasAnswered = this.question.yesIsBad
          ? this.question.numberOfPeopleSelectedYes === 1
          : this.question.numberOfPeopleSelectedYes === 0
        return (
          this.shouldShowFollowUpCheckbox &&
          !this.fullScreen &&
          individualHasAnswered
        )
      } else {
        return this.shouldShowFollowUpCheckbox && !this.fullScreen
      }
    },
    shouldShowMergeFollowupQuestionsCheckbox() {
      return (
        this.shouldShowOwnAnswersCheckbox &&
        this.shouldShowMergeCheckbox &&
        this.showFollowupQuestions
      )
    },
    detailsFlipperEnabled() {
      const isZeroToTenWithRange =
        this.question.type === 'zeroToTen' &&
        this.question.rangeKeys &&
        this.question.rangeKeys.length
      const questionHasHistory = this.question.averages?.length > 1
      return (
        !isZeroToTenWithRange && questionHasHistory && !this.isIndividualGroup
      )
    },
    questionIsPartOfQuizAndCorrectAnswer() {
      return (
        Object.keys(this.question).includes('correctAnswer') &&
        this.question.ownResponse.num === this.question.correctAnswer
      )
    }
  },
  watch: {
    showFollowupQuestions() {
      this.didSelectShowFollowupQuestions({
        question: this.question,
        value: this.showFollowupQuestions
      })
    },
    showFollowupAsMerged() {
      this.didSelectMergeFollowupQuestions({
        question: this.question,
        value: this.showFollowupAsMerged
      })
    }
  },
  mounted() {
    if (
      this.followUpQuestionsState?.questionsWithMergedFollowUp?.hasOwnProperty(
        this.question.name
      )
    ) {
      this.showFollowupAsMerged =
        this.followUpQuestionsState.questionsWithMergedFollowUp[
          this.question.name
        ]
    }

    if (
      this.followUpQuestionsState?.visiblePreconditions?.hasOwnProperty(
        this.question.name
      )
    ) {
      this.showFollowupQuestions =
        this.followUpQuestionsState.visiblePreconditions[this.question.name]
    }
  },
  methods: {}
}
</script>

<style lang="scss" scoped>
.question-details-wrapper {
  height: 100%;
  &.follow-up {
    margin-top: 10px;
    height: calc(100% - 10px);
    .question-card {
      background-color: rgba(var(--kt-gray-200-rgb), 0.5);
    }
  }
}

.question-card {
  // background-color: rgba(255, 255, 255, 0.4);
  background-color: rgba(var(--kt-gray-100-rgb), 0.4);
}
</style>
